const BASE = '/api/admin';

async function adminFetch(path, token, options = {}) {
  const { timeoutMs = 15000, ...fetchOptions } = options;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`${BASE}${path}`, {
      ...fetchOptions,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
    const text = await res.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error(`Error del servidor (${res.status}) — es posible que el endpoint no exista en esta versión desplegada.`);
    }
    if (!data.success) throw new Error(data.error || 'Error del servidor');
    return data.data;
  } finally {
    clearTimeout(timeout);
  }
}

export const getStats = (token) =>
  adminFetch('/stats', token);

export const getOrders = (token, params = {}) =>
  adminFetch(`/orders?${new URLSearchParams(params)}`, token);

export const getOrder = (token, id) =>
  adminFetch(`/orders/${id}`, token);

export const updateOrder = (token, id, body) =>
  adminFetch(`/orders/${id}`, token, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

export const deleteOrder = (token, id) =>
  adminFetch(`/orders/${id}`, token, { method: 'DELETE' });

// Direct sale branded pickup PDF (brand: 'tesoro'). Returns { blob, filename };
// errors come back as the usual JSON body.
export async function downloadBrandedPickupPdf(token, id, brand) {
  const res = await fetch(`${BASE}/orders/${id}?pickupPdf=${encodeURIComponent(brand)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok || !(res.headers.get('Content-Type') || '').includes('application/pdf')) {
    let message = `Error del servidor (${res.status})`;
    try {
      const data = await res.json();
      if (data.error) message = data.error;
    } catch {
      // non-JSON error body — keep the generic message
    }
    throw new Error(message);
  }
  const filename = (res.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/)?.[1] || `recoleccion-${id}.pdf`;
  return { blob: await res.blob(), filename };
}

export const getCustomers = (token, params = {}) =>
  adminFetch(`/customers?${new URLSearchParams(params)}`, token);

// One-off notice email to a single customer: { customerId, subject, body, attachmentPaths? }
export const sendCustomerNotice = (token, payload) =>
  adminFetch('/customer-notice', token, { method: 'POST', body: JSON.stringify(payload), timeoutMs: 30000 });

export const getProducts = (token, params = {}) =>
  adminFetch(`/products?${new URLSearchParams(params)}`, token);

export const getProduct = (token, id) =>
  adminFetch(`/products/${id}`, token);

export const createProduct = (token, body) =>
  adminFetch('/products', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const updateProduct = (token, id, body) =>
  adminFetch(`/products/${id}`, token, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

export const deleteProduct = (token, id) =>
  adminFetch(`/products/${id}`, token, { method: 'DELETE' });

export const createManualSale = (token, body) =>
  adminFetch('/manual-sale', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const getPayments = (token, params = {}) =>
  adminFetch(`/payments?${new URLSearchParams(params)}`, token);

export const createAdminPayment = (token, body) =>
  adminFetch('/payments', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const getPayment = (token, id) =>
  adminFetch(`/payments/${id}`, token);

export const reviewPayment = (token, id, body) =>
  adminFetch(`/payments/${id}`, token, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

export const getPaymentProofSignedUrl = (token, path) =>
  adminFetch(`/payments/signed-url?path=${encodeURIComponent(path)}`, token);

export const getArtists = (token, params = {}) =>
  adminFetch(`/artists?${new URLSearchParams(params)}`, token);

export const getArtist = (token, id) =>
  adminFetch(`/artists/${id}`, token);

export const createArtist = (token, body) =>
  adminFetch('/artists', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const updateArtist = (token, id, body) =>
  adminFetch(`/artists/${id}`, token, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

export const deleteArtist = (token, id) =>
  adminFetch(`/artists/${id}`, token, { method: 'DELETE' });

export const getArtistPayouts = (token, params = {}) =>
  adminFetch(`/artist-payouts?${new URLSearchParams(params)}`, token);

export const createArtistPayout = (token, body) =>
  adminFetch('/artist-payouts', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const deleteArtistPayout = (token, id) =>
  adminFetch(`/artist-payouts/${id}`, token, { method: 'DELETE' });

// On-demand Spanish → EN/ZH/JA translation for admin form "Traducir" buttons.
// `fields` is `{ key: spanishText }`; resolves to `{ translations: { en, zh, ja } }`,
// each locale keyed the same as `fields`. Longer timeout — three Claude calls run
// server-side in parallel.
export const translateAdminFields = (token, fields, locales) =>
  adminFetch('/translate', token, {
    method: 'POST',
    body: JSON.stringify(locales ? { fields, locales } : { fields }),
    timeoutMs: 45000,
  });

// --- Notifications center ---

export const getNotifications = (token, params = {}) =>
  adminFetch(`/notifications?${new URLSearchParams(params)}`, token);

export const getUnreadNotificationCount = (token) =>
  adminFetch('/notifications/unread-count', token);

export const markNotificationRead = (token, id, isRead = true) =>
  adminFetch(`/notifications/${id}`, token, {
    method: 'PATCH',
    body: JSON.stringify({ isRead }),
  });

export const markAllNotificationsRead = (token) =>
  adminFetch('/notifications/mark-all-read', token, { method: 'POST' });

// --- Artist applications (admin review) ---

export const getArtistApplications = (token, params = {}) =>
  adminFetch(`/artist-applications?${new URLSearchParams(params)}`, token);

export const getArtistApplication = (token, id) =>
  adminFetch(`/artist-applications/${id}`, token);

export const approveArtistApplication = (token, id) =>
  adminFetch(`/artist-applications/${id}`, token, {
    method: 'PATCH',
    body: JSON.stringify({ status: 'approved' }),
  });

export const rejectArtistApplication = (token, id, rejectReason) =>
  adminFetch(`/artist-applications/${id}`, token, {
    method: 'PATCH',
    body: JSON.stringify({ status: 'rejected', rejectReason }),
  });

// --- Email events ---

export const getEmailEvents = (token, params = {}) =>
  adminFetch(`/email-events?${new URLSearchParams(params)}`, token);

// --- Usability feedback ---

export const getFeedback = (token, params = {}) =>
  adminFetch(`/feedback?${new URLSearchParams(params)}`, token);

// --- Allies ---

export const getAllies = (token, params = {}) =>
  adminFetch(`/allies?${new URLSearchParams(params)}`, token);

export const getAlly = (token, id) =>
  adminFetch(`/allies/${id}`, token);

export const createAlly = (token, body) =>
  adminFetch('/allies', token, { method: 'POST', body: JSON.stringify(body) });

export const updateAlly = (token, id, body) =>
  adminFetch(`/allies/${id}`, token, { method: 'PUT', body: JSON.stringify(body) });

export const deleteAlly = (token, id) =>
  adminFetch(`/allies/${id}`, token, { method: 'DELETE' });

// --- Ally products ---

export const getAllyProducts = (token, allyId) =>
  adminFetch(`/ally-products?ally_id=${allyId}`, token);

export const getOneAllyProduct = (token, productId) =>
  adminFetch(`/ally-products/${productId}`, token);

export const createAllyProduct = (token, body) =>
  adminFetch('/ally-products', token, { method: 'POST', body: JSON.stringify(body) });

export const updateAllyProduct = (token, productId, body) =>
  adminFetch(`/ally-products/${productId}`, token, { method: 'PUT', body: JSON.stringify(body) });

export const deleteAllyProduct = (token, productId) =>
  adminFetch(`/ally-products/${productId}`, token, { method: 'DELETE' });

// --- Kiosk attract-loop videos ---

export const getKioskLoopVideos = (token) =>
  adminFetch('/kiosk-loop-videos', token);

export const createKioskLoopVideo = (token, body) =>
  adminFetch('/kiosk-loop-videos', token, { method: 'POST', body: JSON.stringify(body) });

export const updateKioskLoopVideo = (token, id, body) =>
  adminFetch(`/kiosk-loop-videos/${id}`, token, { method: 'PATCH', body: JSON.stringify(body) });

export const deleteKioskLoopVideo = (token, id) =>
  adminFetch(`/kiosk-loop-videos/${id}`, token, { method: 'DELETE' });

// --- Taipei Toy Festival pre-order pieces ---

export const getTtfPieces = (token) =>
  adminFetch('/ttf-pieces', token);

export const createTtfPiece = (token, body) =>
  adminFetch('/ttf-pieces', token, { method: 'POST', body: JSON.stringify(body) });

export const updateTtfPiece = (token, id, body) =>
  adminFetch(`/ttf-pieces/${id}`, token, { method: 'PATCH', body: JSON.stringify(body) });

export const deleteTtfPiece = (token, id) =>
  adminFetch(`/ttf-pieces/${id}`, token, { method: 'DELETE' });

export const getTtfPieceRequests = (token) =>
  adminFetch('/ttf-piece-requests', token);

export const updateTtfPieceRequest = (token, id, body) =>
  adminFetch(`/ttf-piece-requests/${id}`, token, { method: 'PATCH', body: JSON.stringify(body) });

export const getTtfOrderDetails = (token, purchaseId) =>
  adminFetch(`/ttf-orders/${purchaseId}`, token);

export const getTtfMetrics = (token) =>
  adminFetch('/ttf-metrics', token);

export const setTtfPaymentLink = (token, purchaseId, mercadoPagoUrl) =>
  adminFetch(`/ttf-orders/${purchaseId}`, token, { method: 'PATCH', body: JSON.stringify({ mercadoPagoUrl }) });

export const setTtfLotteryOutcome = (token, purchaseId, lineIndex, outcome) =>
  adminFetch(`/ttf-orders/${purchaseId}`, token, { method: 'PATCH', body: JSON.stringify({ lineIndex, outcome }) });
